import { css } from '@eti/styles';
import type { ReactNode } from 'react';
import { Inline, Text, textVariants, vars } from '@etg/wings';
import type { StepStatusType } from './constants';
import { StepStatus } from './constants';

const getDescriptionColor = (isCustomerActionRequired: boolean, stepStatus: StepStatusType) => {
  if (isCustomerActionRequired && stepStatus === StepStatus.InProgress) {
    return vars.colors.signal.critical.light;
  }
  if (stepStatus === StepStatus.NotStarted) {
    return '#8c8c8c';
  }
  return vars.colors.text;
};

const descriptionStyles = (isCustomerActionRequired: boolean, stepStatus: StepStatusType) => css`
  color: ${getDescriptionColor(isCustomerActionRequired, stepStatus)};
`;

const iconWrapperStyles = css`
  position: relative;
`;

const infoWrapperStyles = (isLastStep: boolean) => css`
  flex: 1;
  padding-bottom: ${isLastStep ? '0' : '16px'};
`;

const lineStyles = (stepStatus: StepStatusType) => css`
  border-left: 1px ${stepStatus === StepStatus.Completed ? 'solid' : 'dotted'}
    ${stepStatus === StepStatus.Completed ? vars.colors.signal.positive.light : '#8c8c8c'};
  bottom: 0;
  left: calc(50% - 0.5px);
  position: absolute;
  top: 22px;
`;

interface RefundStatusStepProps {
  'data-testid': string;
  title: string | null;
  description: string | null;
  stepIcon: ReactNode | null;
  isLastStep: boolean;
  isCustomerActionRequired: boolean;
  stepStatus: StepStatusType;
  nextStepStatus: StepStatusType | null;
}

const RefundStatusStep = ({
  'data-testid': dataTestId,
  title,
  description,
  stepIcon,
  isLastStep,
  isCustomerActionRequired,
  stepStatus,
  nextStepStatus,
}: RefundStatusStepProps) => {
  const isStepInProgress = stepStatus === StepStatus.InProgress;

  return (
    <Inline data-testid={dataTestId} role="listitem" spacing={8}>
      {stepIcon && (
        <div className={iconWrapperStyles}>
          {stepIcon}
          {!isLastStep && nextStepStatus && (
            <div className={lineStyles(stepStatus)} data-testid={`${dataTestId}-icon-line`} />
          )}
        </div>
      )}
      <div className={infoWrapperStyles(isLastStep)}>
        {title && (
          <Text
            data-testid={`${dataTestId}-title`}
            variant={isStepInProgress ? textVariants._14_BOLD : textVariants._14}
          >
            {title}
          </Text>
        )}
        {description && (
          <Text
            asHtml
            className={descriptionStyles(isCustomerActionRequired, stepStatus)}
            data-testid={`${dataTestId}-description`}
          >
            {description}
          </Text>
        )}
      </div>
    </Inline>
  );
};

export default RefundStatusStep;
